import * as React from 'react';
import { useCallback, useRef, useState } from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva } from 'class-variance-authority';

import { cn } from '../lib/utils';

const tooltipVariants = cva(
  'vai-z-[9999] vai-overflow-hidden vai-rounded-md vai-border vai-bg-popover vai-px-3 vai-py-1.5 vai-text-sm vai-text-popover-foreground vai-shadow-md vai-animate-in vai-fade-in-0 vai-zoom-in-95 data-[state=closed]:vai-animate-out data-[state=closed]:vai-fade-out-0 data-[state=closed]:vai-zoom-out-95 data-[side=bottom]:vai-slide-in-from-top-2 data-[side=left]:vai-slide-in-from-right-2 data-[side=right]:vai-slide-in-from-left-2 data-[side=top]:vai-slide-in-from-bottom-2',
  {
    variants: {
      variant: {
        default: '',
        dark: 'vai-z-[9999] vai-overflow-hidden vai-rounded-md vai-border vai-border-zinc-800 !vai-bg-background-dark vai-px-3 vai-py-1.5 vai-text-sm vai-text-foreground-dark vai-font-inter vai-text-popover-foreground vai-shadow-md vai-animate-in vai-fade-in-0 vai-zoom-in-95 data-[state=closed]:vai-animate-out data-[state=closed]:vai-fade-out-0 data-[state=closed]:vai-zoom-out-95 data-[side=bottom]:vai-slide-in-from-top-2 data-[side=left]:vai-slide-in-from-right-2 data-[side=right]:vai-slide-in-from-left-2 data-[side=top]:vai-slide-in-from-bottom-2 [&>p]:vai-text-sm [&>p]:vai-font-inter [&>p]:vai-font-medium [&>p]:vai-text-foreground-dark [&>span]:vai-text-sm [&>span]:vai-font-inter [&>span]:vai-font-medium [&>span]:vai-text-foreground-dark',
      },
    },
  },
);

const TooltipProvider = TooltipPrimitive.Provider;

// Original Radix Tooltip - used internally for eager mode and fallback behavior
const RadixTooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    portal?: boolean;
    variant?: 'default' | 'dark';
  }
>(
  (
    {
      className,
      sideOffset = 4,
      portal = false,
      variant = 'default',
      ...props
    },
    ref,
  ) => {
    return portal ? (
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          className={cn(tooltipVariants({ variant }), className)}
          {...props}
        />
      </TooltipPrimitive.Portal>
    ) : (
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(tooltipVariants({ variant }), className)}
        {...props}
      />
    );
  },
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * LazyTooltip - A performance-optimized tooltip that only mounts Radix machinery
 * when the tooltip is actually about to be shown.
 *
 * The default Radix Tooltip causes renders during the delay period even before
 * the tooltip is visible. This component avoids that by:
 * 1. Only rendering the trigger element during hover delay
 * 2. Mounting the full Radix Tooltip only when we're ready to show
 *
 * This significantly reduces React commits on mouse movement.
 */
interface LazyTooltipProps {
  children: React.ReactNode;
  delayDuration?: number;
  /** If true, uses the original Radix behavior (for cases where you need it) */
  eager?: boolean;
  /** Controlled open state. When set to false, tooltip is always hidden. */
  open?: boolean;
  /** Callback when open state changes. Useful for analytics tracking. */
  onOpenChange?: (open: boolean) => void;
}

const Tooltip = ({
  children,
  delayDuration = 300,
  eager = false,
  open: controlledOpen,
  onOpenChange,
}: LazyTooltipProps) => {
  const [shouldMount, setShouldMount] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const isMountedRef = useRef(false);
  // Track if pointer is over trigger or content (for interactive tooltips)
  const isPointerInsideRef = useRef(false);

  // If controlled open is explicitly false, never show the tooltip
  const isForceHidden = controlledOpen === false;

  // Track latest controlledOpen value for use in timeout callbacks
  // This prevents stale closure issues when controlledOpen changes during delay
  const controlledOpenRef = useRef(controlledOpen);
  controlledOpenRef.current = controlledOpen;

  // Shared close sequence logic used by both handlePointerLeave and handleContentPointerLeave
  // This starts a timer to close the tooltip with a grace period (100ms) to allow pointer
  // to move between trigger and content, then unmounts after close animation (150ms)
  const scheduleClose = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      // Only close if pointer is still outside both trigger and content
      if (!isPointerInsideRef.current && isMountedRef.current) {
        setIsOpen(false);
        onOpenChange?.(false);
        timeoutRef.current = setTimeout(() => {
          isMountedRef.current = false;
          setShouldMount(false);
        }, 150); // Allow time for close animation
      }
    }, 100); // Grace period to move to content
  }, [onOpenChange]);

  // All hooks must be called unconditionally before any early returns
  // to comply with React's Rules of Hooks
  const handlePointerEnter = useCallback(() => {
    // Don't show tooltip if it's force hidden
    if (isForceHidden) return;

    isPointerInsideRef.current = true;

    // Clear any pending close timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Clear any pending RAF
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    // Start delay timer to mount and show tooltip
    timeoutRef.current = setTimeout(() => {
      // Re-check if tooltip should be hidden - controlledOpen may have changed during delay
      if (controlledOpenRef.current === false) return;

      isMountedRef.current = true;
      setShouldMount(true);
      // Small delay to let React mount, then open
      rafRef.current = requestAnimationFrame(() => {
        // Check again after RAF in case it changed during mount
        if (controlledOpenRef.current === false) return;

        rafRef.current = null;
        setIsOpen(true);
        onOpenChange?.(true);
      });
    }, delayDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- controlledOpenRef is a stable ref
  }, [delayDuration, isForceHidden, onOpenChange]);

  const handlePointerLeave = useCallback(() => {
    isPointerInsideRef.current = false;

    // Clear the open timeout if we leave before it fires
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Cancel any pending RAF to prevent stale setIsOpen(true) after we close
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    // Start the close sequence with grace period
    scheduleClose();
  }, [scheduleClose]);

  // Handler for when pointer enters the tooltip content
  const handleContentPointerEnter = useCallback(() => {
    isPointerInsideRef.current = true;
    // Clear any pending close timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // If tooltip was closing but pointer came back, re-open it
    // This handles the race condition where user quickly moves pointer back
    // during the close animation window (after isOpen was set to false but
    // before the tooltip unmounts)
    if (!isForceHidden) {
      setIsOpen(true);
      onOpenChange?.(true);
    }
  }, [isForceHidden, onOpenChange]);

  // Handler for when pointer leaves the tooltip content
  const handleContentPointerLeave = useCallback(() => {
    isPointerInsideRef.current = false;
    // Start the close sequence with grace period
    scheduleClose();
  }, [scheduleClose]);

  // Clean up on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // If eager mode, use original Radix behavior
  // This return is now after all hooks, satisfying React's Rules of Hooks
  if (eager) {
    return (
      <RadixTooltip
        delayDuration={delayDuration}
        open={controlledOpen}
        onOpenChange={onOpenChange}
      >
        {children}
      </RadixTooltip>
    );
  }

  // Find trigger and content from children
  let trigger: React.ReactElement | null = null;
  let content: React.ReactElement | null = null;
  const childArray = React.Children.toArray(children);

  for (const child of childArray) {
    if (!React.isValidElement(child)) continue;

    // Check by displayName since direct type comparison can fail across module boundaries
    const displayName =
      (child.type as any)?.displayName || (child.type as any)?.name;
    if (displayName === 'TooltipTrigger' || child.type === TooltipTrigger) {
      trigger = child as React.ReactElement;
    } else if (
      displayName === 'TooltipContent' ||
      child.type === TooltipContent
    ) {
      content = child as React.ReactElement;
    }
  }

  if (!trigger) {
    // Fallback to original behavior if structure is unexpected
    // Preserve all props to maintain consistent behavior (e.g., onOpenChange for analytics)
    return (
      <RadixTooltip
        delayDuration={delayDuration}
        open={controlledOpen}
        onOpenChange={onOpenChange}
      >
        {children}
      </RadixTooltip>
    );
  }

  // When not mounted, render only the trigger's children with pointer/focus handlers
  // We can't render TooltipTrigger here because it requires Radix Tooltip context
  if (!shouldMount) {
    const triggerContent = trigger.props.children;

    // Extract props from TooltipTrigger that should be applied to the rendered element
    const { asChild, children: _children, ...triggerProps } = trigger.props;

    // Build shared event handlers
    const sharedHandlers = {
      onPointerEnter: (e: React.PointerEvent) => {
        handlePointerEnter();
        triggerProps.onPointerEnter?.(e);
      },
      onPointerLeave: (e: React.PointerEvent) => {
        handlePointerLeave();
        triggerProps.onPointerLeave?.(e);
      },
      onFocus: (e: React.FocusEvent) => {
        handlePointerEnter();
        triggerProps.onFocus?.(e);
      },
      onBlur: (e: React.FocusEvent) => {
        handlePointerLeave();
        triggerProps.onBlur?.(e);
      },
    };

    // When asChild is true, clone the child element directly (matching Radix asChild behavior)
    if (asChild && React.isValidElement(triggerContent)) {
      // Check if the child is a React Fragment - Fragments cannot receive props like event handlers
      // React.cloneElement on a Fragment will silently ignore any props passed to it
      const isFragment = triggerContent.type === React.Fragment;

      if (isFragment) {
        // Wrap Fragment content in a span so we can attach event handlers
        // This is necessary because Fragments don't render a DOM element and can't receive events
        return (
          <span
            {...triggerProps}
            {...sharedHandlers}
            // Use inline-contents to minimize layout impact - the span acts as if it doesn't exist
            // for layout purposes while still being able to receive events
            style={{ display: 'contents', ...triggerProps.style }}
          >
            {triggerContent}
          </span>
        );
      }

      const childProps = (triggerContent as React.ReactElement<any>).props;

      return React.cloneElement(triggerContent as React.ReactElement<any>, {
        // First spread child's original props to preserve all handlers (onClick, etc.)
        ...childProps,
        // Then spread trigger props (className, data-testid, etc.) - these override child props
        ...triggerProps,
        // Merge classNames if both exist
        className: cn(triggerProps.className, childProps?.className),
        // Compose pointer/focus handlers with any existing handlers from trigger and child
        onPointerEnter: (e: React.PointerEvent) => {
          sharedHandlers.onPointerEnter(e);
          childProps?.onPointerEnter?.(e);
        },
        onPointerLeave: (e: React.PointerEvent) => {
          sharedHandlers.onPointerLeave(e);
          childProps?.onPointerLeave?.(e);
        },
        onFocus: (e: React.FocusEvent) => {
          sharedHandlers.onFocus(e);
          childProps?.onFocus?.(e);
        },
        onBlur: (e: React.FocusEvent) => {
          sharedHandlers.onBlur(e);
          childProps?.onBlur?.(e);
        },
      });
    }

    // When asChild is false/undefined, Radix wraps content in a <button>.
    // We must do the same to maintain consistent DOM structure between states.
    return (
      <button type="button" {...triggerProps} {...sharedHandlers}>
        {triggerContent}
      </button>
    );
  }

  // When mounted, render the full Radix Tooltip
  // Clone content to add pointer handlers for interactive tooltips
  const contentWithHandlers = content
    ? React.cloneElement(content, {
        onPointerEnter: (e: React.PointerEvent) => {
          handleContentPointerEnter();
          content.props.onPointerEnter?.(e);
        },
        onPointerLeave: (e: React.PointerEvent) => {
          handleContentPointerLeave();
          content.props.onPointerLeave?.(e);
        },
      })
    : null;

  return (
    <RadixTooltip
      open={isForceHidden ? false : isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handlePointerLeave();
        } else {
          onOpenChange?.(true);
        }
      }}
      delayDuration={0} // We already handled the delay
    >
      <TooltipTrigger
        {...trigger.props}
        onPointerEnter={(e: React.PointerEvent) => {
          handleContentPointerEnter();
          trigger.props.onPointerEnter?.(e);
        }}
        onPointerLeave={(e: React.PointerEvent) => {
          handlePointerLeave();
          trigger.props.onPointerLeave?.(e);
        }}
        onFocus={(e: React.FocusEvent) => {
          // Clear pending close timeouts on keyboard focus (mirrors unmounted state behavior)
          handleContentPointerEnter();
          trigger.props.onFocus?.(e);
        }}
        onBlur={(e: React.FocusEvent) => {
          handlePointerLeave();
          trigger.props.onBlur?.(e);
        }}
      />
      {contentWithHandlers}
    </RadixTooltip>
  );
};

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
